import React, { ReactElement, memo, useMemo } from "react";
import cx from "classnames";

// text
import { AriaHidden } from "@swiggy-private/core-ui";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

import { CART_ITEMS_TEST_IDS } from "@swiggy-private/cart-test-ids";

import styles from "./styles/FoodItemInfo.scss";
import { SWIGGY_ONE_ICONS_HW_SMALL } from "@swiggy-private/super-ui";
import { PlanDetails } from "@swiggy-private/cart-types";
import { isSwiggyOneLitePlan, isSwiggyOnePlanVariants } from "@cart-ui/helpers/OfferBundle";
import { isEmpty } from "@swiggy-private/js-utils";
import { BoldText } from "./constants";

export interface ExtraItemInfoProps {
    // name of item
    name?: string;
    // optional subtext if any
    subText?: string;
    planDetails?: PlanDetails;
    logo?: string;
}

const ExtraItemInfo = (props: ExtraItemInfoProps): ReactElement | null => {
    const { planDetails, subText } = props;

    const { isOneProgram, isOneLitePlan } = useMemo(() => {
        const isOneProgram = isSwiggyOnePlanVariants(planDetails as PlanDetails);
        const isOneLitePlan = isOneProgram && isSwiggyOneLitePlan(planDetails?.type || "");
        return { isOneProgram, isOneLitePlan };
    }, [planDetails]);

    const formattedSubText: string = useMemo(() => {
        if (isEmpty(subText)) {
            return "";
        }

        if (!isOneProgram) {
            return subText;
        }

        const hasBoldText = subText.includes(BoldText.START) && subText.includes(BoldText.END);

        if (!hasBoldText) {
            return subText;
        }

        const [text, secondaryText] = subText.split(BoldText.START).map(item => item.trim());
        const [boldText, lastText] = secondaryText.split(BoldText.END).map(item => item.trim());

        return `${text} <span class=${styles.containerOneSubTextBold}>${boldText}</span> ${lastText}`;
    }, [isOneProgram, subText]);

    const { iconWidth, iconHeight } = useMemo(() => {
        const iconWidth = isOneLitePlan
            ? SWIGGY_ONE_ICONS_HW_SMALL.ONE_LITE.width
            : SWIGGY_ONE_ICONS_HW_SMALL.ONE.width;

        const iconHeight = isOneLitePlan
            ? SWIGGY_ONE_ICONS_HW_SMALL.ONE_LITE.height
            : SWIGGY_ONE_ICONS_HW_SMALL.ONE.height;
        return { iconWidth, iconHeight };
    }, [isOneLitePlan]);

    const logoClasses = useMemo(
        () => ({
            [styles.containerBoxPlus]: isOneProgram,
            [styles.containerBoxLite]: isOneLitePlan,
        }),
        [isOneLitePlan, isOneProgram],
    );

    const custombackground = useMemo(() => ({ light: "", dark: "" }), []);

    if (!props.name) return null;

    return (
        <div className={cx(styles.container)}>
            <AriaHidden
                as="div"
                className={cx(styles.containerName, { [styles.containerOneName]: isOneProgram })}
                data-testid={CART_ITEMS_TEST_IDS.CART_ITEM_NAME}
            >
                {isOneProgram && !isEmpty(props.logo) && (
                    <div className={cx(styles.containerBox, logoClasses)}>
                        <Image
                            cloudinaryId={props?.logo}
                            lazyLoad={false}
                            width={iconWidth}
                            height={iconHeight}
                            customBackground={custombackground}
                            alt="one-program-logo"
                        />
                    </div>
                )}

                {props.name}
            </AriaHidden>
            {props.subText && (
                <div
                    className={cx(styles.containerFreebieText, { [styles.containerOneSubText]: isOneProgram })}
                    dangerouslySetInnerHTML={{ __html: formattedSubText }}
                    data-testid={CART_ITEMS_TEST_IDS.CART_ITEM_SUBTEXT}
                />
            )}
        </div>
    );
};

export default memo(ExtraItemInfo);
